import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { 
  ArrowLeft,
  Search,
  Filter,
  User,
  Mail,
  Calendar,
  FileText,
  GraduationCap,
  Activity,
  Clock,
  ChevronRight,
  Download,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';

const API_URL = window.location.origin;

// Color mapping for activity types
const ACTIVITY_COLORS = {
  blue: 'bg-blue-100 text-blue-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  green: 'bg-green-100 text-green-600',
  violet: 'bg-violet-100 text-violet-600',
  amber: 'bg-amber-100 text-amber-600',
  slate: 'bg-slate-100 text-slate-600',
  orange: 'bg-orange-100 text-orange-600',
  rose: 'bg-rose-100 text-rose-600',
  purple: 'bg-purple-100 text-purple-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal: 'bg-teal-100 text-teal-600',
  gray: 'bg-gray-100 text-gray-600',
  sky: 'bg-sky-100 text-sky-600'
};

const AdminStudentActivityPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');

  useEffect(() => {
    const fetchStudentHistory = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/activity/admin/user/${userId}/full-history?limit=100`);
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching student history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentHistory();
  }, [userId]);

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!data || !data.student) {
    return (
      <AdminLayout>
        <div className="p-6 text-center">
          <p className="text-gray-500">Student not found</p>
          <Button onClick={() => navigate('/admin/students')} className="mt-4">
            Back to Students
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const { student, activities, applications, search_history, total_activities } = data;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Activity Log</h1>
            <p className="text-gray-500">Complete history and audit trail</p>
          </div>
        </div>

        {/* Student Profile Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={student.picture} />
                <AvatarFallback className="bg-gradient-to-br from-ux-cyan to-ux-blue text-white text-xl">
                  {student.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">{student.name}</h2>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {student.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Joined {formatDate(student.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    {total_activities} activities
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {student.is_blocked && (
                    <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                  )}
                  {student.has_profile && (
                    <Badge className="bg-green-100 text-green-700">Profile Complete</Badge>
                  )}
                  {applications?.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {applications.length} Applications
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-sm text-gray-500">User ID</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">{student.user_id}</code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity Log ({activities?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Applications ({applications?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="searches" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search History ({search_history?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile Data
            </TabsTrigger>
          </TabsList>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {activities?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No activities recorded yet</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {activities?.map((activity, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${ACTIVITY_COLORS[activity.color] || ACTIVITY_COLORS.gray}`}>
                          <Activity className="w-5 h-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-medium text-gray-900">{activity.title}</p>
                              {activity.description && (
                                <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                              )}
                              <Badge variant="outline" className="mt-2 text-xs">
                                {activity.label || activity.activity_type}
                              </Badge>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm text-gray-500">{formatTime(activity.created_at)}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatDate(activity.created_at)}</p>
                            </div>
                          </div>
                          
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                            <details className="mt-2">
                              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                View metadata
                              </summary>
                              <pre className="mt-2 p-2 bg-white rounded text-xs overflow-x-auto">
                                {JSON.stringify(activity.metadata, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Application History</CardTitle>
              </CardHeader>
              <CardContent>
                {applications?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No applications yet</p>
                ) : (
                  <div className="space-y-4">
                    {applications?.map((app, index) => (
                      <div 
                        key={index}
                        className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{app.university_name}</h3>
                            <p className="text-sm text-gray-600">{app.program}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Intake: {app.intake} | Country: {app.country}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge className={`${
                              app.current_stage === 'visa_granted' ? 'bg-green-100 text-green-700' :
                              app.current_stage === 'offer_received' ? 'bg-violet-100 text-violet-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {app.current_stage?.replace(/_/g, ' ')}
                            </Badge>
                            <p className="text-xs text-gray-400 mt-2">
                              Applied {formatTime(app.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {app.status_updates?.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-2">Status Updates</p>
                            <div className="space-y-2">
                              {app.status_updates.map((update, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                                  <span className="text-gray-600">{update.stage?.replace(/_/g, ' ')}</span>
                                  <span className="text-gray-400 text-xs">{formatTime(update.timestamp)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Search History Tab */}
          <TabsContent value="searches">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Search History</CardTitle>
              </CardHeader>
              <CardContent>
                {search_history?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No searches recorded</p>
                ) : (
                  <div className="space-y-3">
                    {search_history?.map((search, index) => (
                      <div 
                        key={index}
                        className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-gray-900">
                                {search.session_name || 'Unnamed Search'}
                              </span>
                              {search.saved && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Saved</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Session ID: {search.session_id}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">{formatTime(search.created_at)}</p>
                            {search.universities_count > 0 && (
                              <p className="text-xs text-gray-400 mt-1">
                                {search.universities_count} results
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Data Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Profile Data</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Basic Information</h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Name</dt>
                        <dd className="text-gray-900">{student.name || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Email</dt>
                        <dd className="text-gray-900">{student.email || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Phone</dt>
                        <dd className="text-gray-900">{student.phone || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Auth Provider</dt>
                        <dd className="text-gray-900 capitalize">{student.auth_provider || 'email'}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Account Status */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Account Status</h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Search Count</dt>
                        <dd className="text-gray-900">{student.search_count || 0}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Is Blocked</dt>
                        <dd className="text-gray-900">{student.is_blocked ? 'Yes' : 'No'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Has Profile</dt>
                        <dd className="text-gray-900">{student.has_profile ? 'Yes' : 'No'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Created</dt>
                        <dd className="text-gray-900">{formatDate(student.created_at)}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Documents */}
                  {student.documents && (
                    <div className="md:col-span-2">
                      <h4 className="font-medium text-gray-700 mb-3">Uploaded Documents</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(student.documents).map(([key, value]) => (
                          value?.uploaded && (
                            <div key={key} className="p-2 bg-green-50 rounded text-sm">
                              <span className="text-green-700 capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminStudentActivityPage;
